import i18n from "@/plugins/i18n";
import QInputRuleMixin from "@/helpers/Mixins/QInputRuleMixin.js"

const rules = QInputRuleMixin.data();

const groupKeys = Object.freeze({
    ServiceProvider: 'ServiceProvider',
    Job: 'Job',
    TimeReport: 'TimeReport',
    Article: 'Article',
    ImportExport: 'ImportExport',
    NewLead: 'NewLead',
    PartnerAppointment: 'PartnerAppointment',
    Contract: 'Contract',
    Permissions: 'Permissions',
    TA_Restrictions: 'TA_Restrictions',
    Store: 'Store',
});

const obj = Object.freeze({

    AddressStreet: { key: "ServiceProvider.AddressStreet", value: "" },
    AddressZipAndCity: { key: "ServiceProvider.AddressZipAndCity", value: "" },
    MainLocationCoordinates: { key: "MainLocationCoordinates", value: null },

    ServiceProviderTelephone: {
        settingKey: 'ServiceProvider.Telephone',
        group: groupKeys.ServiceProvider,
        label: i18n.t('servicesystem.common_objects.company_settings.telephone'),
        description: 'Contact phone number for your service department.',
        globalSettingsKey: null,
        type: String,
        component: null,
        rules: [rules.phoneRule],
    },
    IsSerialNumberEditingRestricted: {
        settingKey: 'IsSerialNumberEditingRestricted',
        group: groupKeys.TA_Restrictions,
        label: 'Restrict serial number editing',
        description: 'Restricts modification of serial numbers from the technician app when enabled.',
        globalSettingsKey: null,
        type: Boolean,
        component: 'QSwitchInput',
        rules: [],
    },
    ServiceProviderCompanyName: {
        settingKey: 'ServiceProvider.CompanyName',
        group: groupKeys.ServiceProvider,
        label: i18n.t('servicesystem.common_objects.company_settings.company_name'),
        description: 'The name of your company.',
        globalSettingsKey: null,
        type: String,
        component: null,
        rules: [rules.requiredRule],
    },
    ServiceProviderEmail: {
        settingKey: 'ServiceProvider.Email',
        group: groupKeys.ServiceProvider,
        label: i18n.t('servicesystem.common_objects.company_settings.email'),
        description: 'Your service department email address.',
        globalSettingsKey: null,
        type: String,
        component: null,
        rules: [rules.emailRule],
    },
    ServiceProviderAddressStreet: {
        settingKey: 'ServiceProvider.AddressStreet',
        group: groupKeys.ServiceProvider,
        label: i18n.t('servicesystem.common_objects.company_settings.street_address'),
        description: '',
        globalSettingsKey: null,
        type: String,
        component: null,
        rules: [],
    },
    ServiceProviderAddressZipAndCity: {
        settingKey: 'ServiceProvider.AddressZipAndCity',
        group: groupKeys.ServiceProvider,
        label: i18n.t('servicesystem.common_objects.company_settings.postcode_and_city'),
        description: '',
        globalSettingsKey: null,
        type: String,
        component: null,
        rules: [],
    },
    ServiceProviderMainLocationCoordinates: {
        settingKey: 'MainLocationCoordinates',
        group: groupKeys.ServiceProvider,
        label: 'Geolocation',
        description: '',
        globalSettingsKey: 'Maps.MainLocationCoordinates',
        type: String,
        component: null,
        rules: [],
    },

    DefaultEstimatedTime: {
        settingKey: 'DefaultEstimatedTime',
        group: groupKeys.Job,
        label: i18n.t('servicesystem.common_objects.job.time_estimate'),
        description: 'Default estimation of time spent when a job is created. Example, two hours: 2',
        globalSettingsKey: 'DefaultJobEstimatedTime',
        type: Number,
        component: 'QTextField',
        rules: [rules.requiredRule, rules.floatRule],
    },
    DefaultPriorityId: {
        settingKey: 'DefaultPriorityId',
        group: groupKeys.Job,
        label: i18n.t('servicesystem.common_objects.job.priority'),
        description: '',
        globalSettingsKey: 'DefaultJobPriorityId',
        type: Number,
        component: 'JobPrioritySelect',
        rules: [rules.requiredRule],
    },
    DefaultJobTypeId: {
        settingKey: 'DefaultJobTypeId',
        group: groupKeys.Job,
        label: i18n.t('servicesystem.common_objects.job.type'),
        description: '',
        globalSettingsKey: 'DefaultJobTypeId',
        type: Number,
        component: 'JobTypeSelect',
        rules: [rules.requiredRule],
    },
    DefaultStatusId: {
        settingKey: 'DefaultStatusId',
        group: groupKeys.Job,
        label: i18n.t('servicesystem.common_objects.job.status'),
        description: '',
        globalSettingsKey: 'DefaultJobStatusId',
        type: Number,
        component: 'JobStatusSelect2',
        rules: [rules.requiredRule],
    },
    TechnicianCanApproveJob: {
        settingKey: 'Permissions.TechnicianCanApproveJob',
        group: groupKeys.Permissions,
        label: 'Allow technicians to approve jobs',
        description: 'Administrators can always approve jobs. \nIf enabled, technicians can also approve jobs. \nOther user roles can never approve jobs.',
        globalSettingsKey: 'TechnicianCanApproveJob',
        type: Boolean,
        component: 'QSwitchInput',
        rules: [],
    },
    TechnicianLimitSelectToOwnStore: {
        settingKey: 'TechnicianLimitSelectToOwnStore',
        group: groupKeys.Store,
        label: 'Lock part warehouse selection',
        description: 'Limit warehouse options to only the user\'s personal warehouse/car. \nPersonal warehouse is set in the user account menu.',
        globalSettingsKey: 'TechnicianLimitSelectToOwnStore',
        type: Boolean,
        component: 'QSwitchInput',
        rules: [],
    },
    DefaultJobCategoryId: {
        settingKey: 'DefaultJobCategoryId',
        group: groupKeys.Job,
        label: i18n.t('servicesystem.common_objects.job.job_category'),
        description: '',
        globalSettingsKey: 'DefaultJobCategoryId',
        type: Number,
        component: 'JobCategorySelect2',
        rules: [],
    },

    InternalArticles: {
        settingKey: 'InternalArticles',
        group: groupKeys.Article,
        label: 'Internal articles',
        description: '',
        globalSettingsKey: null,
        type: String,
        component: 'QTextField',
        rules: [],
    },

    TimeReportInternalCustomer: {
        settingKey: 'TimeReportInternalCustomer',
        group: groupKeys.TimeReport,
        label: 'Internal order customer',
        description: 'Customer used to track internal time spent.',
        globalSettingsKey: null,
        type: String,
        component: 'CustomerSelect2',
        rules: [rules.requiredRule],
    },

    TimeReportDefaultWorkArticle: {
        settingKey: 'TimeReportDefaultWorkArticle',
        group: groupKeys.TimeReport,
        label: 'Work time default article',
        description: 'The article to use for work time entries by default.',
        globalSettingsKey: null,
        type: String,
        component: 'ArticleSelect2',
        rules: [rules.requiredRule],
    },
    TimeReportDefaultDrivingArticle: {
        settingKey: 'TimeReportDefaultDrivingArticle',
        group: groupKeys.TimeReport,
        label: 'Driving time default article',
        description: 'The article to use for driving time entries by default.',
        globalSettingsKey: null,
        type: String,
        component: 'ArticleSelect2',
        rules: [rules.requiredRule],
    },
    TimeReportDefaultDrivingCostParkingArticle: {
        settingKey: 'TimeReportDefaultDrivingCostParkingArticle',
        group: groupKeys.TimeReport,
        label: 'Parking article',
        description: '',
        globalSettingsKey: null,
        type: String,
        component: 'ArticleSelect2',
        rules: [],
    },
    TimeReportDefaultDrivingCostRoadTaxArticle: {
        settingKey: 'TimeReportDefaultDrivingCostRoadTaxArticle',
        group: groupKeys.TimeReport,
        label: 'Road tax article',
        description: '',
        globalSettingsKey: null,
        type: String,
        component: 'ArticleSelect2',
        rules: [],
    },
    TimeReportDefaultDrivingCostZoneArticle: {
        settingKey: 'TimeReportDefaultDrivingCostZoneArticle',
        group: groupKeys.TimeReport,
        label: 'Driving zone cost article',
        description: '',
        globalSettingsKey: null,
        type: String,
        component: 'ArticleSelect2',
        rules: [],
    },
    TimeReportDefaultDrivingDictanceArticle: {
        settingKey: 'TimeReportDefaultDrivingDictanceArticle',
        group: groupKeys.TimeReport,
        label: 'Kilometer article',
        description: '',
        globalSettingsKey: null,
        type: String,
        component: 'ArticleSelect2',
        rules: [],
    },
    TimeReportDefaultGenericArticle: {
        settingKey: 'TimeReportDefaultGenericArticle',
        group: groupKeys.TimeReport,
        label: 'Generic article',
        description: '',
        globalSettingsKey: null,
        type: String,
        component: 'ArticleSelect2',
        rules: [],
    },
    TimeReportDefaultMiscPartCostArticle: {
        settingKey: 'TimeReportDefaultMiscPartCostArticle',
        group: groupKeys.TimeReport,
        label: 'Misc part cost article',
        description: '',
        globalSettingsKey: null,
        type: String,
        component: 'ArticleSelect2',
        rules: [],
    },
    TimeReportValidDrivingArticles: {
        settingKey: 'TimeReportValidDrivingArticles',
        group: groupKeys.TimeReport,
        label: 'All valid driving time articles',
        description: '',
        globalSettingsKey: null,
        type: String,
        component: 'QTextField',
        rules: [],
    },
    TimeReportValidWorkArticles: {
        settingKey: 'TimeReportValidWorkArticles',
        group: groupKeys.TimeReport,
        label: 'All valid work time articles',
        description: '',
        globalSettingsKey: null,
        type: String,
        component: 'QTextField',
        rules: [],
    },
    TimeReportIncludeTimeDescriptionWorkDrive: {
        settingKey: 'TimeReportIncludeTimeDescriptionWorkDrive',
        group: groupKeys.TimeReport,
        label: 'Show times in text',
        description: 'Include start–end times (for example 08:00–09:30) in the line text.',
        globalSettingsKey: null,
        type: Boolean,
        component: 'QSwitchInput',
        rules: [],
    },
    TimeReportDriveMinValueTimeRounding: {
        settingKey: 'TimeReportDriveMinValueTimeRounding',
        group: groupKeys.TimeReport,
        label: 'Minimum drive duration per entry (minutes)',
        description: 'If the recorded drive duration is shorter, we still bill at least this many minutes.',
        globalSettingsKey: null,
        type: Number,
        component: 'QTextField',
        rules: [rules.requiredRule, rules.floatRule],
    },
    TimeReportDriveTimeRounding: {
        settingKey: 'TimeReportDriveTimeRounding',
        group: groupKeys.TimeReport,
        label: 'Start time rounding (minutes)',
        description: 'Rounds the start time down to the nearest step. \nDoes not affect the total duration. \nAllowed values: 0, 1, 5, 10, 15, 20, 30, 60. \nExample: with 15, a start at 12:17 becomes 12:15.',
        globalSettingsKey: null,
        type: Number,
        component: 'QTextField',
        rules: [rules.requiredRule, rules.floatRule],
    },
    TimeReportDriveTimeMinutesIncurred: {
        settingKey: 'TimeReportDriveTimeMinutesIncurred',
        group: groupKeys.TimeReport,
        label: 'Duration rounding step after minimum (minutes)',
        description: 'Rounds the total duration up to the nearest multiple of this value once the minimum is met. \nExample: with 30, 55 minutes becomes 60 minutes.',
        globalSettingsKey: null,
        type: Number,
        component: 'QTextField',
        rules: [rules.requiredRule, rules.floatRule],
    },
    TimeReportWorkMinValueTimeRounding: {
        settingKey: 'TimeReportWorkMinValueTimeRounding',
        group: groupKeys.TimeReport,
        label: 'Minimum work duration per entry (minutes)',
        description: 'If the recorded work duration is shorter, we still bill at least this many minutes (applies before duration rounding).',
        globalSettingsKey: null,
        type: Number,
        component: 'QTextField',
        rules: [rules.requiredRule, rules.floatRule],
    },
    TimeReportWorkTimeRounding: {
        settingKey: 'TimeReportWorkTimeRounding',
        group: groupKeys.TimeReport,
        label: 'Start time rounding (minutes)',
        description: 'Rounds the start time down to the nearest step. \nDoes not round the duration. \nAllowed values: 0, 1, 5, 10, 15, 20, 30, 60. \nExample: with 15, a start at 12:17 becomes 12:15',
        globalSettingsKey: null,
        type: Number,
        component: 'QTextField',
        rules: [rules.requiredRule, rules.floatRule],
    },
    TimeReportWorkTimeMinutesIncurred: {
        settingKey: 'TimeReportWorkTimeMinutesIncurred',
        group: groupKeys.TimeReport,
        label: 'Duration rounding step after minimum (minutes)',
        description: 'Rounds the total duration up to the nearest multiple of this value once the minimum is met. \nExample: with 30, 55 minutes becomes 60 minutes.',
        globalSettingsKey: null,
        type: Number,
        component: 'QTextField',
        rules: [rules.requiredRule, rules.floatRule],
    },

    ApiImportFailedEmail: {
        settingKey: 'ApiImportFailedEmail',
        group: groupKeys.ImportExport,
        label: 'Recipient address for import failure notifications',
        description: '',
        globalSettingsKey: null,
        type: String,
        component: 'QTextField',
        rules: [],
    },
    ApiImportSucceededEmail: {
        settingKey: 'ApiImportSucceededEmail',
        group: groupKeys.ImportExport,
        label: 'Recipient address for import success notifications',
        description: '',
        globalSettingsKey: null,
        type: String,
        component: 'QTextField',
        rules: [],
    },
    ImportExportImportProgress: {
        settingKey: 'ImportExportImportProgress',
        group: groupKeys.ImportExport,
        label: 'Progress state of last import stored in database',
        description: '',
        globalSettingsKey: null,
        type: String,
        component: 'QTextField',
        rules: [],
    },


    ContractArticles: {
        settingKey: 'ContractArticles',
        group: groupKeys.Contract,
        label: 'List of articles used as contract articles.',
        description: '',
        globalSettingsKey: null,
        type: String,
        component: 'QTextField',
        rules: [],
    },
    ContractAutomaticOrders: {
        settingKey: 'ContractAutomaticOrders',
        group: groupKeys.Contract,
        label: '',
        description: '',
        globalSettingsKey: null,
        type: Boolean,
        component: 'QTextField',
        rules: [],
    },
    ContractAutomaticOrdersStartPaymentDaysOffset: {
        settingKey: 'ContractAutomaticOrdersStartPaymentDaysOffset',
        group: groupKeys.Contract,
        label: '',
        description: '',
        globalSettingsKey: null,
        type: Number,
        component: 'QTextField',
        rules: [],
    },
    ContractBatchOrders: {
        settingKey: 'ContractBatchOrders',
        group: groupKeys.Contract,
        label: '',
        description: '',
        globalSettingsKey: null,
        type: Boolean,
        component: 'QTextField',
        rules: [],
    },
    ContractCreditOrderTextPrefix: {
        settingKey: 'ContractCreditOrderTextPrefix',
        group: groupKeys.Contract,
        label: '',
        description: '',
        globalSettingsKey: null,
        type: String,
        component: 'QTextField',
        rules: [],
    },
    ContractCustomerTextArticle: {
        settingKey: 'ContractCustomerTextArticle',
        group: groupKeys.Contract,
        label: '',
        description: '',
        globalSettingsKey: null,
        type: String,
        component: 'QTextField',
        rules: [],
    },
    ContractDefaultInvoiceLengthMonths: {
        settingKey: 'ContractDefaultInvoiceLengthMonths',
        group: groupKeys.Contract,
        label: '',
        description: '',
        globalSettingsKey: null,
        type: Number,
        component: 'QTextField',
        rules: [],
    },
    ContractDefaultLengthMonths: {
        settingKey: 'ContractDefaultLengthMonths',
        group: groupKeys.Contract,
        label: '',
        description: '',
        globalSettingsKey: null,
        type: Number,
        component: 'QTextField',
        rules: [],
    },
    ContractDocumentTemplateIds: {
        settingKey: 'ContractDocumentTemplateIds',
        group: groupKeys.Contract,
        label: '',
        description: '',
        globalSettingsKey: null,
        type: String,
        component: 'QTextField',
        rules: [],
    },
    ContractMachineTextArticle: {
        settingKey: 'ContractMachineTextArticle',
        group: groupKeys.Contract,
        label: '',
        description: '',
        globalSettingsKey: null,
        type: String,
        component: 'QTextField',
        rules: [],
    },
    ContractPeriodTextArticle: {
        settingKey: 'ContractPeriodTextArticle',
        group: groupKeys.Contract,
        label: '',
        description: '',
        globalSettingsKey: null,
        type: String,
        component: 'QTextField',
        rules: [],
    },
    ShowNewContractViews: {
        settingKey: 'ShowNewContractViews',
        group: groupKeys.Contract,
        label: '',
        description: '',
        globalSettingsKey: 'ShowNewContractViews',
        type: Boolean,
        component: 'QTextField',
        rules: [],
    },
    UpdateContractPaymentPlan: {
        settingKey: 'UpdateContractPaymentPlan',
        group: groupKeys.Contract,
        label: '',
        description: '',
        globalSettingsKey: null,
        type: Boolean,
        component: 'QTextField',
        rules: [],
    },

    NewLeadEmailEnabled: {
        settingKey: 'NewLeadEmailEnabled',
        group: groupKeys.NewLead,
        label: '',
        description: '',
        globalSettingsKey: null,
        type: Boolean,
        component: 'QTextField',
        rules: [],
    },
    NewLeadEmailTo: {
        settingKey: 'NewLeadEmailTo',
        group: groupKeys.NewLead,
        label: '',
        description: '',
        globalSettingsKey: null,
        type: String,
        component: 'QTextField',
        rules: [],
    },
    NewLeadEmailFrom: {
        settingKey: 'NewLeadEmailFrom',
        group: groupKeys.NewLead,
        label: '',
        description: '',
        globalSettingsKey: null,
        type: String,
        component: 'QTextField',
        rules: [],
    },
    NewLeadEmailSubject: {
        settingKey: 'NewLeadEmailSubject',
        group: groupKeys.NewLead,
        label: '',
        description: '',
        globalSettingsKey: null,
        type: String,
        component: 'QTextField',
        rules: [],
    },
    NewLeadEmailBody: {
        settingKey: 'NewLeadEmailBody',
        group: groupKeys.NewLead,
        label: '',
        description: '',
        globalSettingsKey: null,
        type: String,
        component: 'QTextField',
        rules: [],
    },

    CompletedJobEmail: {
        settingKey: 'CompletedJobEmail',
        group: groupKeys.Job,
        label: '',
        description: '',
        globalSettingsKey: null,
        type: Boolean,
        component: 'QTextField',
        rules: [],
    },
    CompletedJobEmailFrom: {
        settingKey: 'CompletedJobEmailFrom',
        group: groupKeys.Job,
        label: '',
        description: '',
        globalSettingsKey: null,
        type: String,
        component: 'QTextField',
        rules: [],
    },
    CompletedJobEmailSubject: {
        settingKey: 'CompletedJobEmailSubject',
        group: groupKeys.Job,
        label: '',
        description: '',
        globalSettingsKey: null,
        type: String,
        component: 'QTextField',
        rules: [],
    },
    CompletedJobEmailBody: {
        settingKey: 'CompletedJobEmailBody',
        group: groupKeys.Job,
        label: '',
        description: '',
        globalSettingsKey: null,
        type: String,
        component: 'QTextField',
        rules: [],
    },


    PartnerAppointmentInformationEmailSubject: {
        settingKey: 'PartnerAppointmentInformationEmailSubject',
        group: groupKeys.PartnerAppointment,
        label: '',
        description: '',
        globalSettingsKey: null,
        type: String,
        component: 'QTextField',
        rules: [],
    },
    PartnerAppointmentInformationEmailBody: {
        settingKey: 'PartnerAppointmentInformationEmailBody',
        group: groupKeys.PartnerAppointment,
        label: '',
        description: '',
        globalSettingsKey: null,
        type: String,
        component: 'QTextField',
        rules: [],
    },
});

export default obj;